import { makeStyles } from "tss-react/mui";
import {
  BOX_SELECTOR_SIZES,
  BOX_SELECTOR_VARIANTS,
} from "@miplatform/constants";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Fade,
  Radio,
  Typography,
} from "@mui/material";

/**
 * A selectable card component with checkbox, radio, or plain text options.
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - The main label text.
 * @param {string} props.description - The secondary text below the label.
 * @param {"checkbox" | "radio" | "none"} props.variant - The type of selection control.
 * @param {"big" | "small"} props.size - The size of the card (defaults to "big").
 * @param {boolean} props.selected - Whether the card is selected.
 * @param {boolean} props.disabled - Whether the card is disabled.
 * @param {number | string} props.width - The width of the card.
 * @param {JSX.Element} [props.icon] - Optional icon to be displayed.
 * @param {Function} [props.onChange] - Callback when selection changes.
 * @param {(
 *   "primary" | "secondary" | "teal" | "blue" | "green" | "red" | "white" | "black" |
 *   "cyan" | "grey" | "purple" | "deepPurple" | "indigo" | "orange" | "bluegrey" |
 *   "amber" | "pink" | "lightblue" | "yellow"
 * )} [props.color="primary"] - Theme color (defaults to "primary").
 *
 * @example
 * import { MiBoxSelector } from "./MiBoxSelector";
 * import { useState } from "react";
 * import CheckIcon from "@mui/icons-material/Check";
 *
 * const MyComponent = () => {
 *   const [selected, setSelected] = useState(false);
 *
 *   return (
 *     <MiBoxSelector
 *       label="Option 1"
 *       description="This is a selectable option."
 *       variant="checkbox"
 *       size="big"
 *       selected={selected}
 *       onChange={setSelected}
 *       color="teal"
 *       icon={<CheckIcon />}
 *     />
 *   );
 * };
 *
 * export default MyComponent;
 */

export const MiBoxSelector = ({
  label,
  description,
  variant = BOX_SELECTOR_VARIANTS.RADIO,
  size = BOX_SELECTOR_SIZES.BIG,
  width = 192,
  selected = false,
  disabled,
  icon,
  onChange,
  color = "primary",
}) => {
  const { classes } = useStyles({
    color,
    width,
    size,
    variant,
    selected,
    disabled,
  });

  const handleChange = () => {
    if (onChange && !disabled) {
      onChange(!selected);
    }
  };

  return (
    <Card variant="outlined" className={classes.card} onClick={handleChange}>
      <Box className={classes.contentWrapper}>
        <Box style={{ width: 0 }}>
          {variant === BOX_SELECTOR_VARIANTS.CHECKBOX &&
            size === BOX_SELECTOR_SIZES.BIG && (
              <Fade in={selected}>
                <Checkbox
                  checked={selected}
                  disabled={disabled}
                  color={color}
                />
              </Fade>
            )}
          {variant === BOX_SELECTOR_VARIANTS.RADIO &&
            size === BOX_SELECTOR_SIZES.BIG &&
            selected && (
              <Fade in={selected}>
                <Radio checked={selected} disabled={disabled} color={color} />
              </Fade>
            )}
        </Box>
        <Box className={classes.content}>
          {icon && <Box className={classes.iconContainer}>{icon}</Box>}
          <CardContent className={classes.textContent}>
            {label && (
              <Typography className={classes.label}>{label}</Typography>
            )}
            {description && (
              <Typography className={classes.description}>
                {description}
              </Typography>
            )}
          </CardContent>
        </Box>
      </Box>
    </Card>
  );
};

const useStyles = makeStyles()(
  (theme, { color, width, size, selected, disabled }) => ({
    card: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: theme.spacing(size === BOX_SELECTOR_SIZES.BIG ? 2 : 1),
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: theme.palette.grey.lightAccent,
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      cursor: "pointer",
      backgroundColor: "inherit",
      width,
      ...(selected &&
        !disabled && {
          backgroundColor: theme.palette[color]?.light,
          borderColor: theme.palette[color]?.main,
        }),
      ...(disabled
        ? {
            cursor: "default",
            opacity: 0.4,
            backgroundColor: theme.palette.grey.darkAccent,
          }
        : {
            "&:hover": {
              backgroundColor: theme.palette[color]?.light,
              borderColor: theme.palette[color]?.main,
            },
          }),
    },
    contentWrapper: {
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
      justifyContent: "flex-start",
      width: "100%",
      gap: theme.spacing(2),
    },
    content: {
      ...(size === BOX_SELECTOR_SIZES.BIG
        ? {
            margin: "auto",
          }
        : {
            display: "flex",
            width: "100%",
          }),
    },
    textContent: {
      textAlign: "center",
      padding: "0 !important",
      ...(size === BOX_SELECTOR_SIZES.SMALL && {
        margin: "auto",
      }),
    },
    label: {
      fontSize: theme.fontSize.medium,
      fontWeight: 600,
    },
    description: {
      fontSize: theme.fontSize.small,
      color: theme.palette.text.secondary,
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      ...(size === BOX_SELECTOR_SIZES.BIG && {
        padding: theme.spacing(1),
        justifyContent: "center",
      }),
    },
  })
);
